import React, { useState, type ReactElement } from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../../theme/theme';
import PlusIconSvg from '../../../assets/img/plus-icon.svg';
import InvitationModal from '../InvitationModal/InvitationModal';

import { useAuth } from '../../../context/AuthContext';
import { UsageType } from '../../../utils/enums/usage-type.enum';
import LimitModal from './LimitModal/LimitModal';

interface SubordinatesTabsProps {
  tabs: { label: string; children: ReactElement }[];
  activeTab: number;
  onChange: any;
}

export default function SubordinatesTabs({ tabs, activeTab, onChange }: SubordinatesTabsProps): ReactElement {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const userLimit = user.usage.find((item: { type: UsageType }) => item.type === UsageType.USER_LIMIT).limit;
  const subordinateUsage = user.usage.find((item: { type: UsageType }) => item.type === UsageType.USER_LIMIT).usage;
  const hasExceededUserLimit = user.isNccUser ? false : userLimit <= subordinateUsage;

  const openInviteModal = () => {
    if (hasExceededUserLimit) {
      setIsLimitModalOpen(true);
    } else {
      setIsInviteModalOpen(true);
    }
  };

  const handleManageSubscription = () => {
    navigate('/manage-subscription');
  };

  return (
    <Box display="flex" width="100%" flexDirection="column" height="100%" overflow="hidden">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        borderBottom="1px solid #DCE0E5"
        padding="0 16px"
      >
        <Tabs
          orientation="horizontal"
          value={activeTab}
          onChange={(event, newValue) => onChange(newValue)}
          sx={{ alignItems: 'flex-start' }}
        >
          {tabs.map((tab, _index) => (
            <Tab
              key={tab.label}
              label={tab.label}
              sx={{
                color: activeTab === _index ? `${theme.palette.shades.lightBlueColor}` : 'text.secondary',
                fontWeight: 'normal',
                textTransform: 'none',
              }}
            />
          ))}
        </Tabs>
        {activeTab === tabs.findIndex((tab) => tab.label === 'Invitations') && (
          <Box
            display="flex"
            gap={1}
            alignItems="center"
            style={{ paddingLeft: 30, paddingRight: 30, cursor: 'pointer' }}
            onClick={openInviteModal}
          >
            <Box component="img" src={PlusIconSvg} alt="plus-icon" />
            <Typography variant="textH6" color={theme.palette.common.black}>
              Invite Subordinates
            </Typography>
          </Box>
        )}
      </Box>

      <Box flexGrow={1} overflow="auto" padding={2}>
        {tabs[activeTab].children}
      </Box>
      <InvitationModal open={isInviteModalOpen} setIsInviteModalOpen={setIsInviteModalOpen} />
      <LimitModal
        handleManageSubscription={handleManageSubscription}
        isLimitModalOpen={isLimitModalOpen}
        setIsLimitModalOpen={setIsLimitModalOpen}
        aria-labelledby="limit-modal-title"
      />
    </Box>
  );
}
